import Vue from 'vue';
import { mapGetters } from 'vuex';
import path from 'path';
import _ from 'lodash';
import { guid } from '@/mixins/guid';
import { modals } from '@/mixins/modal';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import tooltip from '@/mixins/components/tooltip';
import linkDynamic from '@editor/droplets/_bubble-options/components/link-dynamic';
import linkPage from '@editor/droplets/_bubble-options/components/link-page';

export default Vue.component('edit-video-player', {
  template: require('./template.html'),
  computed: {
    ...mapGetters(['viewport']),
    displayName() {
      return path.basename(this.video.src);
    },
  },
  mixins: [guid, modals, uploader],
  components: {
    uploadProgress,
    tooltip,
    linkDynamic,
    linkPage,
  },
  data() {
    return {
      loading: false,
      bucketPath: '_droplet-assets/videoPlayer/',
      thumbBucketPath: '_droplet-assets/videoPlayer/thumbs/',
      prefix: 'drz_video_',
      s3Url: process.env.S3_ASSETS_URL,
      showFiles: false,
      videoFiles: [],
      activeVideo: null,
      showConfirm: false,
      bounceTime: 500,
      uploaders: {
        video: false,
        thumb: false,
      },
      display: {
        playsinline: false,
        muted: false,
        autoplay: false,
        loop: false,
        hideControls: false,
        hideProgress: false,
        enableThumb: false,
        latestDynamic: false,
        hasLink: false,
      },
    };
  },
  name: 'edit-video-player',
  props: [
    'droplet',
    'show-edit-bubble',
    'backup-thumb',
    'dynamic-item',
    'checks',
    'video',
  ],
  watch: {
    showEditBubble(open) {
      if (open) {
        this.setInitialValues();
      }
    },
  },
  methods: {
    setInitialValues() {
      this.display.playsinline = this.droplet.playsinline;
      this.display.muted = this.droplet.muted;
      this.display.autoplay = this.droplet.autoplay;
      this.display.loop = this.droplet.loop;
      this.display.hideControls = this.droplet.hideControls;
      this.display.hideProgress = this.droplet.hideProgress;
      this.display.enableThumb = this.droplet.enableThumb;
      this.display.latestDynamic = this.droplet.latestDynamic;
      this.display.hasLink = this.droplet.hasLink;
    },
    updateVideo(key) {
      this.$store.dispatch('setDropletVal', {
        drp: this.droplet,
        key,
        val: this.display[key],
      });
      this._debounce(() => this.bouncePlugin(), this.bounceTime);
    },
    matchFile(file) {
      const f = file.split('/').pop();
      const media = this.droplet.videoFile.split('/').pop();
      return f === media;
    },
    toggleBrowse() {
      this.showFiles = !this.showFiles;
      if (this.showFiles) {
        this.fetchVideoFiles();
      }
    },
    async fetchVideoFiles() {
      try {
        this.loading = true;
        const files = await this._s3fetchFiles(`${this._sitePath}/${this.bucketPath}`);
        this.videoFiles = files;
      } catch (err) {
        this.$log.error('error: ', err);
        this.showFiles = false;
        await this._reportError({
          error: err,
          fn: 'fetchVideoFiles',
        });
      } finally {
        this.loading = false;
      }
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    openThumb() {
      this.$refs.uploadThumb.click();
    },
    onLink() {
      this._debounce(() => this.bouncePlugin(), this.bounceTime);
    },
    onUnlink() {
      this._debounce(() => this.bouncePlugin(), this.bounceTime);
    },
    shortId() {
      const generate = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
      return `${generate()}${generate()}`;
    },
    async uploadThumb(e) {
      const validated = await this.$validator.validate('thumbnail');
      if (validated) {
        this.uploaders.thumb = true;
        this.uploaders.video = false;
        let newFile = [...e.target.files];
        if (newFile.length > 0) {
          newFile = newFile[0];
          // clean up the file name for s3
          let fileName = path.basename(newFile.name);
          fileName = `${this.shortId()}_${_.trim(fileName)}`;
          fileName = _.replace(fileName, /\s+/g, '_');
          try {
            // send off to our s3 upload mixin
            const filePath = `/${this.thumbBucketPath}${fileName}`;
            await this.uploadFile({
              file: `${this._sitePath}${filePath}`,
              body: newFile,
            });
            this.$store.dispatch('setDropletVal', {
              drp: this.droplet,
              key: 'thumbnail',
              val: filePath,
            });
          } catch (err) {
            if (!err.message.match(/aborted/gi)) {
              this.$log.error(err);
            }
          }
        }
        // reset value to null so we can get the "change" event to run
        // in any case. Even when the user selects the same file
        e.target.value = null;
        this.uploaders.thumb = false;
      }
    },
    async uploadVideo(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          this.uploaders.thumb = false;
          this.uploaders.video = true;
          let newFile = [...e.target.files];
          if (newFile.length > 0) {
            newFile = newFile[0];
            // clean up the file name for s3
            let fileName = path.basename(newFile.name);
            fileName = `${this.prefix}${_.trim(fileName)}`;
            fileName = _.replace(fileName, /\s+/g, '_');
            try {
              // send off to our s3 upload mixin
              const filePath = `/${this.bucketPath}${fileName}`;
              await this.uploadFile({
                file: `${this._sitePath}${filePath}`,
                body: newFile,
              });
              await this.updateVideoSrc(filePath);
            } catch (err) {
              if (!err.message.match(/aborted/gi)) {
                this.$log.error(err);
                await this._reportError({
                  error: err,
                  fn: 'uploadVideo',
                });
              }
            } finally {
              if (this.showFiles) {
                this.fetchVideoFiles();
              }
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          e.target.value = null;
          this.uploaders.video = false;
        }
      });
    },
    toggleConfirm() {
      this.showConfirm = !this.showConfirm;
      const content = this.$refs.showConfirm;
      if (this.showConfirm) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
    },
    confirmDelete(file) {
      this.activeVideo = file;
      this.toggleConfirm();
    },
    async deleteVideoFile() {
      // prep notifcation type
      const notif = {
        id: this.guid(),
      };
      const file = path.basename(this.activeVideo);
      try {
        this.loading = true;
        // delete original
        await this._s3deleteFile({
          key: this.activeVideo,
        });
        // set for notifications
        notif.type = 'success';
        notif.message = `Successfully deleted video file: ${file}`;
      } catch (err) {
        this.$log.error('error: ', err);
        // set for notifications
        notif.type = 'error';
        notif.message = `${err.message || err}. This has been reported.`;
        await this._reportError({
          error: err,
          fn: 'deleteVideoFile',
        });
      } finally {
        // send off to notifications
        this.$store.dispatch('addNotification', notif);
        // kill loader & refetch files
        this.loading = false;
        this.fetchVideoFiles();
      }
      // if deleting video that is being used, reset it to default
      if (this.droplet.videoFile === `/${this.bucketPath}${file}`) {
        this.updateVideoSrc(`${process.env.S3_ASSETS_URL}multi-media/love-coding.mp4`, true);
      }
    },
    async updateVideoSrc(f, def) {
      // if came from click, gather full url
      let file;
      if (def) {
        file = f;
      } else {
        file = `/${this.bucketPath}${this._getFromThumb(path.basename(f))}`;
      }
      // destroy plugin first (parent component method)
      this.$vnode.context.unBindPlugin();
      const payload = {};
      payload.drp = this.droplet;
      payload.videoFile = file;
      this.$store.dispatch('setVideoFile', payload);
      // rebind plugin (parent component method)
      this.$vnode.context.bindPlugin();
    },
    setLocals(v) {
      this.$emit('set-display', v);
    },
    async drzRevertData() {
      // bounce plugin on cancel
      try {
        if (!this._isBoilerplateAsset(this.droplet.videoFile)) {
          let file = path.basename(this.droplet.videoFile);
          file = `${this._sitePath}/${this.bucketPath}${file}`;
          await this._s3checkFile({ path: file });
        }
      } catch (e) {
        this.$log.warn('warning, file not there to revert: ', e);
        // this is for the event in which a user deletes
        // a video from s3 then tries to cancel. Since we
        // aren't restoring the file in s3, we need to
        // reset to the default video
        this.updateVideoSrc(`${process.env.S3_ASSETS_URL}multi-media/love-coding.mp4`, true);
      } finally {
        this.$emit('css-generator');
        this.bouncePlugin();
      }
    },
    bouncePlugin() {
      this.$vnode.context.unBindPlugin();
      this.$vnode.context.bindPlugin();
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/video-player/components/_edit-options/index.js